import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { chequeScanEndpoints } from '@/lib/api/endpoints';
import { uuidv7 } from '@/lib/api/clients/billing-v2';
import { getActiveTenantId } from '@/lib/activeTenant';
import {
  depositKeys,
  estimateKeys,
  invoiceKeys,
  jobKeys,
  paymentKeys,
  receiptKeys,
} from '@/lib/queryKeys';
import { invalidateInvoicePaymentViews } from '@/hooks/invoices/invalidateInvoicePaymentViews';

/** What a scanned check is deposited against: an invoice, an estimate's
 *  deposit, or a part of a job's deposit. */
export type ChequeScanTarget =
  | { kind: 'invoice'; invoiceId: string }
  | { kind: 'estimate_deposit'; estimateId: string }
  | { kind: 'job_deposit'; jobId: string };

/** `GET payments/cheque-scan/availability`. `reason` is shown as-is. */
export interface ChequeScanAvailability {
  available: boolean;
  reason: string | null;
}

/** What the claim route takes. `accountNumber` is the FULL account number the
 *  reader took off the MICR line: sent once, here, and never kept or shown. */
export interface ChequeScanClaimBody {
  amount: number;
  idempotencyKey: string;
  captureId: string;
  routingNumber: string;
  accountNumber: string;
  checkNumber: string;
  ocr: {
    amount: number | null;
    amountConfidence: number | null;
    micrConfidence: number | null;
    amountDiscrepancyDetected: boolean;
    endorsementDetected: boolean;
    checkType: number | null;
  };
  customerNoticeConfirmed: true;
  paymentMethodDiscountGrossAmount?: number;
}

/** The PENDING row the component will debit against. */
export interface ChequeScanClaim {
  paymentId: string;
  amount: number;
  idempotencyKey: string;
  /** The component must send this as `customerData.customerNumber`. */
  customerNumber: string | null;
  accountLast4: string;
  checkNumber: string;
}

/** The server's answer to confirm / fail — the row as the ledger left it. */
export interface ChequeScanSettlement {
  paymentId: string;
  status: 'SUCCEEDED' | 'PENDING' | 'FAILED';
  paymentTransId: string | null;
  detail: string | null;
}

// Keep unresolved scans' keys across dialog remounts in this browser session,
// the way `useChargeSavedInvoicePayment` does: a claim whose response was lost
// must be replayed, not opened a second time — a second row for the same
// paper would be refused as a duplicate check.
const pendingScanKeys = new Map<string, string>();

function scanSignature(
  target: ChequeScanTarget,
  captureId: string,
  amount: number,
  gross?: number,
) {
  // An invoice is named by its bare id, as before deposits could be scanned; a
  // deposit carries its kind, so none of them collide.
  const subject =
    target.kind === 'invoice'
      ? target.invoiceId
      : target.kind === 'estimate_deposit'
        ? `estimate_deposit:${target.estimateId}`
        : `job_deposit:${target.jobId}`;
  return JSON.stringify([getActiveTenantId(), subject, captureId, amount, gross ?? null]);
}

/** The idempotency key for this scan: the one it already has, or a new one. */
export function chequeScanIdempotencyKey(
  target: ChequeScanTarget,
  captureId: string,
  amount: number,
  gross?: number,
): string {
  const signature = scanSignature(target, captureId, amount, gross);
  const existing = pendingScanKeys.get(signature);
  if (existing) return existing;
  const prefix =
    target.kind === 'invoice'
      ? 'invoice-cheque-scan'
      : target.kind === 'estimate_deposit'
        ? 'estimate-deposit-cheque-scan'
        : 'job-deposit-cheque-scan';
  const key = `${prefix}:${uuidv7()}`;
  pendingScanKeys.set(signature, key);
  return key;
}

/** The scan reached an outcome (settled, or failed before any charge): a later
 *  scan of the same capture is a new claim. */
export function releaseChequeScanIdempotencyKey(idempotencyKey: string): void {
  for (const [signature, key] of pendingScanKeys) {
    if (key === idempotencyKey) pendingScanKeys.delete(signature);
  }
}

export function useChequeScanAvailability(
  options: { enabled?: boolean; refetchOnWindowFocus?: boolean } = {},
) {
  return useApiQuery<ChequeScanAvailability>({
    ...(options.refetchOnWindowFocus === false ? { refetchOnWindowFocus: false } : {}),
    queryKey: paymentKeys.chequeScanAvailability(),
    queryFn: () =>
      apiGet<ChequeScanAvailability>(operationsClient, chequeScanEndpoints.availability()),
    enabled: options.enabled ?? true,
    // Reported, not thrown: an unavailable rail is an answer, and a failed
    // request just means the option is not offered.
    showErrorToast: false,
    staleTime: 60_000,
  });
}

/**
 * What a deposit changes — the same queries a manually recorded payment
 * refreshes (`useRecordPayment`), plus the payments list it now appears in.
 */
export async function invalidateAfterChequeScan(
  queryClient: QueryClient,
  invoiceId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: receiptKeys.all() }),
    queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) }),
    queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: [...invoiceKeys.detail(invoiceId), 'payments'] }),
    queryClient.invalidateQueries({ queryKey: paymentKeys.lists() }),
    invalidateInvoicePaymentViews(queryClient),
  ]);
}

/**
 * What a check scanned for an estimate's deposit changes — the same queries a
 * staff deposit capture refreshes (`useCaptureDeposit`): the estimate, which
 * decides whether Collect deposit is offered again, and the deposit ledger the
 * clearing deposit appears in. Plus the payments list the row shows in.
 */
export async function invalidateAfterEstimateDepositChequeScan(
  queryClient: QueryClient,
  estimateId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: receiptKeys.all() }),
    queryClient.invalidateQueries({ queryKey: estimateKeys.detail(estimateId) }),
    queryClient.invalidateQueries({ queryKey: estimateKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: depositKeys.all() }),
    queryClient.invalidateQueries({ queryKey: paymentKeys.lists() }),
  ]);
}

/**
 * What a check scanned for a job's deposit changes — the same queries a job
 * deposit capture refreshes (`useCaptureDeposit`): the job, its payments list
 * the scan shows in, the financial summary its Billing card reads the deposit
 * (paid, clearing, due) from, and its invoices, which a deposit can reach.
 * Plus the deposit ledger and the payments lists.
 */
export async function invalidateAfterJobDepositChequeScan(
  queryClient: QueryClient,
  jobId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: receiptKeys.all() }),
    queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) }),
    queryClient.invalidateQueries({ queryKey: jobKeys.payments(jobId) }),
    queryClient.invalidateQueries({ queryKey: jobKeys.financialSummary(jobId) }),
    queryClient.invalidateQueries({ queryKey: invoiceKeys.byJobPrefix(jobId) }),
    queryClient.invalidateQueries({ queryKey: depositKeys.all() }),
    queryClient.invalidateQueries({ queryKey: paymentKeys.lists() }),
  ]);
}

/**
 * Refetches what a scan pays — the invoice, or the estimate's or job's
 * deposit — and the payment lists it shows in.
 */
export function useInvalidateScanTarget() {
  const queryClient = useQueryClient();
  return (target: ChequeScanTarget) => {
    switch (target.kind) {
      case 'invoice':
        return invalidateAfterChequeScan(queryClient, target.invoiceId);
      case 'estimate_deposit':
        return invalidateAfterEstimateDepositChequeScan(queryClient, target.estimateId);
      case 'job_deposit':
        return invalidateAfterJobDepositChequeScan(queryClient, target.jobId);
    }
  };
}

function claimEndpoint(target: ChequeScanTarget): string {
  switch (target.kind) {
    case 'invoice':
      return chequeScanEndpoints.claimForInvoice(target.invoiceId);
    case 'estimate_deposit':
      return chequeScanEndpoints.claimForEstimateDeposit(target.estimateId);
    case 'job_deposit':
      return chequeScanEndpoints.claimForJobDeposit(target.jobId);
  }
}

/** Open the claim against the scan's target: an invoice, or a deposit. */
export function useClaimChequeScan() {
  return useApiMutation<ChequeScanClaim, { target: ChequeScanTarget; body: ChequeScanClaimBody }>({
    mutationFn: ({ target, body }) =>
      apiPost<ChequeScanClaim, ChequeScanClaimBody>(operationsClient, claimEndpoint(target), body),
  });
}

export function useConfirmChequeScan() {
  const invalidate = useInvalidateScanTarget();
  return useApiMutation<
    ChequeScanSettlement,
    { target: ChequeScanTarget; paymentId: string; paymentTransId: string }
  >({
    mutationFn: ({ paymentId, paymentTransId }) =>
      apiPost<ChequeScanSettlement>(operationsClient, chequeScanEndpoints.confirm(paymentId), {
        paymentTransId,
      }),
    // Money may have moved whatever the answer: the row is on the target.
    onSettled: (_data, _error, { target }) => invalidate(target),
  });
}

export function useFailChequeScan() {
  const invalidate = useInvalidateScanTarget();
  return useApiMutation<
    ChequeScanSettlement,
    { target: ChequeScanTarget; paymentId: string; reason: string }
  >({
    mutationFn: ({ paymentId, reason }) =>
      apiPost<ChequeScanSettlement>(operationsClient, chequeScanEndpoints.fail(paymentId), {
        reason,
      }),
    onSettled: (_data, _error, { target }) => invalidate(target),
  });
}
